import React, { useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import { Close } from "@mui/icons-material";
import PropTypes from "prop-types";

// A simple wrapper for inline SVG icons to keep the main component clean.
const InfoIcon = ({ children }) => <div className="text-gray-400">{children}</div>;

InfoIcon.propTypes = {
  children: PropTypes.node.isRequired,
};

// Image slider component for multiple images
const ImageSlider = ({ fileUrls }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const isImageFile = (url) => /\.(jpe?g|png|gif|bmp|webp)$/i.test(url);
  const isPdfFile = (url) => /\.pdf$/i.test(url);

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? fileUrls.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) => (prev === fileUrls.length - 1 ? 0 : prev + 1));
  };

  const currentFileUrl = fileUrls[currentImageIndex];

  return (
    <div className="relative">
      <div className="bg-gray-50 p-0.5 rounded-lg min-h-96 flex items-center justify-center">
        {isImageFile(currentFileUrl) ? (
          <img
            src={currentFileUrl}
            alt={`Receipt ${currentImageIndex + 1}`}
            className="w-full h-auto max-h-96 object-contain rounded-md"
          />
        ) : (
          <div className="text-center">
            <InfoIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 mx-auto text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </InfoIcon>
            <p className="mt-2 text-sm text-gray-600">
              {isPdfFile(currentFileUrl) ? "PDF Document" : "File"}
            </p>
            <a
              href={currentFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors outline-none border-none"
            >
              View File
            </a>
          </div>
        )}
      </div>

      {/* Navigation buttons - only show if more than 1 image */}
      {fileUrls.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-md transition-all duration-200 border-none outline-none"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-md transition-all duration-200 border-none outline-none"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Image counter - only show if more than 1 image */}
      {fileUrls.length > 1 && (
        <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm">
          {currentImageIndex + 1} / {fileUrls.length}
        </div>
      )}
    </div>
  );
};

ImageSlider.propTypes = {
  fileUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
};

// --- MAIN COMPONENT ---
export const ViewReceiptDetailsModal = ({
  showModal,
  setShowModal,
  receiptData,
  darkMode = false,
}) => {
  if (!receiptData) return null;

  // Destructure for cleaner access
  const { fileUrls } = receiptData;

  const isImageFile = (url) => /\.(jpe?g|png|gif|bmp|webp)$/i.test(url);

  const handleClose = () => setShowModal(false);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "560px",
    maxWidth: "90%",
    bgcolor: !darkMode ? "#fff" : "#202940",
    boxShadow: 10,
    p: "5px",
    borderRadius: "12px",
    outline: "none",
    "@media (max-width:767px)": {
      width: "95%",
      maxHeight: "90vh",
      overflow: "auto",
    },
  };

  return (
    <Modal
      open={showModal}
      onClose={handleClose}
      aria-labelledby="receipt-modal-title"
      aria-describedby="receipt-modal-description"
    >
      <Box sx={style}>
        {/* Header */}
        <div
          className="border-b border-gray-200 p-2.5 flex justify-between items-center"
          style={{
            borderColor: darkMode ? "#374151" : "#e5e7eb",
            backgroundColor: darkMode ? "#202940" : "#fff",
          }}
        >
          <h2
            id="receipt-modal-title"
            className="text-lg font-bold m-0"
            style={{ color: darkMode ? "#fff" : "#1f2937" }}
          >
            Receipt Details
          </h2>
          <div
            style={{ cursor: "pointer", color: darkMode ? "#9ca3af" : "#6b7280" }}
            onClick={handleClose}
          >
            <Close />
          </div>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-4" style={{ backgroundColor: darkMode ? "#202940" : "#fff" }}>
          <div className="space-y-4">
            <h3
              className="text-base font-medium pb-2 border-b border-gray-200"
              style={{
                color: darkMode ? "#fff" : "#1f2937",
                borderColor: darkMode ? "#374151" : "#e5e7eb",
              }}
            >
              Uploaded Receipt{" "}
              {fileUrls && fileUrls.length > 1
                ? `Images (${fileUrls.length})`
                : isImageFile(fileUrls?.[0])
                ? "Image"
                : "File"}
            </h3>

            {fileUrls && fileUrls.length > 0 ? (
              <ImageSlider fileUrls={fileUrls} />
            ) : (
              <div className="bg-gray-50 p-0.5 rounded-lg min-h-96 flex items-center justify-center">
                <p className="text-sm text-gray-500">No file available</p>
              </div>
            )}
          </div>
        </div>
      </Box>
    </Modal>
  );
};

ViewReceiptDetailsModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  darkMode: PropTypes.bool,
  receiptData: PropTypes.shape({
    id: PropTypes.string,
    fileUrls: PropTypes.arrayOf(PropTypes.string),
    status: PropTypes.oneOf(["WAITING_APPROVAL", "APPROVED", "REJECTED"]),
    extractedData: PropTypes.shape({
      orderID: PropTypes.string,
      orderDate: PropTypes.string,
      deliveryDate: PropTypes.string,
      totalAmountInCents: PropTypes.number,
      subTotalInCents: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      customerPhoneNumber: PropTypes.string,
      customerEmail: PropTypes.string,
    }),
    createdAt: PropTypes.string,
    _customer_customer: PropTypes.shape({
      id: PropTypes.string,
      fullName: PropTypes.string,
    }),
  }),
};
